import {keyframes} from '@emotion/react'
import {remCalc} from '_themeUtils'

const zoomImgAnimation = keyframes`
    from {
        transform: scale(1.2);
    }
    to {
        transform: scale(1);
    }
`

const enterTextAnimation = keyframes`
    from {
        transform: translateX(150%);
    }
    to {
        transform: translateX(0);
    }
`

const animationUnderlineAfter = keyframes`
    0% {
        transform: translateX(0);
    }
    50% {
        transform: translateX(100%);
    }
    51% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(0);
    }
`

const cardPhotoVideo = {
    CardPhotoVideo: {
        baseStyle: ({
            padding,
            color,
            textAlign,
            verticalAlign,
            horizontalAlign,
            isImgZoom,
            animationTime,
            isAnimationTitle,
            variant,
            isLeft,
            isAnimationCta,
            imgDeskRatio,
            imgMobileRatio,
            imgTabletRatio,
        }) => ({
            container: {
                position: 'relative',
                maxW: '100%',
                maxH: '100%',
                h: '100%',
                color: color && color.length ? color : variant !== 'padded' ? 'color.text.white' : 'color.text.black',
                justifyContent: 'center',
                gap: 'spacing.0',
                mx: padding === true ? '5%' : '0',
                overflow: 'hidden',
                my: padding === true ? {base: '5%', md: '0'} : '0',
                pl: isLeft && variant === 'padded' ? {base: 0, md: '5%', lg: '8%'} : '0',
                pr: !isLeft && variant === 'padded' ? {base: 0, md: '5%', lg: '8%'} : '0',
            },
            link: {
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 3,
            },
            body: {
                textAlign: textAlign,
                alignItems: textAlign,
                minW: '24dvw',
                top: verticalAlign == 'top' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                bottom: verticalAlign == 'bottom' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                left: horizontalAlign == 'left' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                right: horizontalAlign == 'right' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                gap: 'spacing.none',
                overflow: 'hidden',
                maxW: {base: `calc( 100% - ${remCalc(64)})`, xl: '50%'},

                '.cardPhotoVideo__btn--text a': {
                    color: color && color.length ? color : variant !== 'padded' ? 'color.text.white' : 'color.text.button.textLink.default',
                    border: 'unset',
                    overflow: 'hidden',
                    letterSpacing: remCalc(3),
                    fontWeight: 400,
                    background: 'transparent',
                    px: 0,
                    _after: {
                        content: '""',
                        pos: 'absolute',
                        width: '100%',
                        h: remCalc(1),
                        bottom: 0,
                        left: 0,
                        background: color && color.length ? color : variant !== 'padded' ? 'color.border.white' : 'color.border.button.textLink',
                        animation: isAnimationCta ? `${animationUnderlineAfter} ${animationTime}ms cubic-bezier(.25,.1,.25,1) forwards` : 'none',
                    },
                },
                '.cardPhotoVideo__btn--button a': {
                    backgroundColor: color && color.length ? color : 'color.background.button.primary.default',
                    color: 'color.text.button.primary.default',
                    letterSpacing: remCalc(4),
                    textStyle: 'buttonLinkMd',
                    py: remCalc(11),
                    px: remCalc(33),
                    w: 'fit-content',
                    _hover: {
                        backgroundColor: color && color.length ? color : 'color.background.button.primary.hover',
                        color: 'color.text.button.primary.hover',
                    },
                },
                '.cardPhotoVideo__btn--outline a': {
                    letterSpacing: remCalc(4),
                    color: color && color.length ? color : 'color.text.button.outline.default',
                    borderColor: color && color.length ? color : 'color.border.button.outline.default',
                    py: remCalc(11),
                    px: remCalc(33),
                    textStyle: 'buttonLinkMd',
                    w: 'fit-content',
                    _hover: {
                        color: color && color.length ? color : 'color.text.button.outline.default',
                        borderColor: color && color.length ? color : 'color.border.button.outline.hover',
                        backgroundColor: color && color.length ? 'color.background.transparent' : 'color.background.button.outline.hover',
                    },
                },
            },
            image: {
                width: '100%',
                height: 0,
                pt:
                    imgDeskRatio || imgTabletRatio || imgMobileRatio
                        ? {
                              base: `${imgMobileRatio || imgTabletRatio || imgDeskRatio || 125}%`,
                              md: `${imgTabletRatio || imgDeskRatio || 125}%`,
                              xl: `${imgDeskRatio || 125}%`,
                          }
                        : '125%',
                overflow: 'hidden',
                position: 'relative',
                img: {
                    animation: isImgZoom ? `${zoomImgAnimation} ${animationTime}ms cubic-bezier(.25,.1,.25,1)` : 'none',
                    objectPosition: 'top',
                },
            },
            video: {
                width: '100%',
                height: 'calc(100% + 1px)',
                overflow: 'hidden',
                // pt: '125%',
            },
            title: {
                textStyle: 'displayXs',
                fontWeight: 400,
                letterSpacing: remCalc(1),
                fontFamily: 'GTEesti',
                animation: isAnimationTitle ? `${enterTextAnimation} ${animationTime}ms cubic-bezier(.25,.1,.25,1)` : 'none',
            },
            text: {
                mb: 'spacing.6',
                textStyle: {base: 'paragraphSm', md: 'paragraphLg'},
            },
        }),
        variants: {
            fullOneThird: {
                container: {
                    height: '100%',
                },
                body: {
                    position: 'absolute',
                    overflow: 'hidden',
                    pointerEvents: 'all',
                    width: {base: `calc( 100% - ${remCalc(48)})`, md: '50%'},
                    maxWidth: remCalc(420),

                    '.cardPhotoVideo__btn--text a': {
                        py: remCalc(10),
                        minH: remCalc(43),
                        h: remCalc(43),
                    },
                    '.cardPhotoVideo__btn--button a': {
                        width: 'fit-content',
                        py: remCalc(11),
                        px: remCalc(33),
                    },
                },
                title: {
                    textStyle: {base: 'displayXs', lg: 'displaySm'},
                    mb: 'spacing.4',
                },
                bodyWrapper: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'color.background.alpha.dark.30',
                    pointerEvents: 'none',
                },
                text: {
                    textStyle: 'paragraphLg',
                },
                image: {
                    height: 'fill-available',
                    pt: 0,
                },
            },
            full: {
                body: {
                    position: 'absolute',
                    overflow: 'hidden',
                    pointerEvents: 'all',
                    '.cardPhotoVideo__btn--text a': {
                        py: remCalc(10),
                        minH: remCalc(43),
                        h: remCalc(43),
                    },
                },
                title: {
                    mb: 'spacing.4',
                },
                bodyWrapper: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'color.background.alpha.dark.30',
                    pointerEvents: 'none',
                },
            },
            padded: {
                container: {
                    p: {base: '10% 5%', md: '7% 5%', lg: '10% 5%'},
                },
                body: {
                    textAlign: 'left',
                    width: '100%',
                    '.cardPhotoVideo__btn--outline': {
                        mx: '10%',
                        width: 'fill-available',
                        a: {
                            w: '100%',
                        },
                    },
                    '.cardPhotoVideo__btn--button': {
                        mx: '10%',
                        width: 'fill-available',
                        a: {
                            w: '100%',
                        },
                    },
                    '.cardPhotoVideo__btn--text': {
                        width: 'fit-content',
                        mx: 'auto',
                        a: {
                            textStyle: 'displayXs',
                            pb: remCalc(7),
                            pt: 0,
                            minH: remCalc(32),
                            h: remCalc(32),
                            fontWeight: 350,
                        },
                    },
                },
                bodyWrapper: {
                    width: '100%',
                },

                title: {
                    mt: 'spacing.4',
                    mb: 'spacing.2',
                },
            },
        },
    },
}

export default cardPhotoVideo
